import { IEventLoggingComponentProps, withEventLogging } from '@AppModels/Logging';
import { IOperationResultNoValue, Industry } from '@ViewModels';
import { StyleDeclarationValue, css } from 'aphrodite';
import { computed, makeObservable } from 'mobx';
import { Observer, inject, observer } from 'mobx-react';
import * as React from 'react';
import { DragDropContext, DropResult, Droppable, DroppableProvided } from 'react-beautiful-dnd';
import { ISystemEmailTemplate } from '../../../../models/AdminModels';
import { ErrorMessagesViewModelKey, IErrorMessageComponentProps } from '../../../../models/AppState';
import { IndustryTemplateViewModel, IndustryTemplatesViewModel } from '../../../../viewmodels/AdminViewModels';
import { LoadingSpinner } from '../../../../web/components/LoadingSpinner';
import {
	INotificationServiceComponentProps,
	withNotificationService,
} from '../../../../web/components/LocalNotificationObserver/WithNotificationService';
import { IndustryTemplatesTableRow } from '../IndustryTemplatesTableRow';
import { styleSheet } from './styles';

interface IProps<T extends IndustryTemplateViewModel = IndustryTemplateViewModel>
	extends IEventLoggingComponentProps,
		IErrorMessageComponentProps,
		INotificationServiceComponentProps<ISystemEmailTemplate> {
	className?: string;
	industry: Industry;
	nameColumnLabel?: string;
	onMenuItemClicked?(template: T, menuItem: 'edit' | 'archive' | 'delete' | 'publish' | 'copy'): void;
	onTemplateClicked?(template: T, e: React.MouseEvent<HTMLElement>): void;
	styles?: StyleDeclarationValue[];
	templates: IndustryTemplatesViewModel;
	type: 'drafts' | 'archived' | 'published';
}

class _IndustryTemplatesTable<T extends IndustryTemplateViewModel = IndustryTemplateViewModel> extends React.Component<
	IProps<T>
> {
	constructor(props: IProps<T>) {
		super(props);

		makeObservable<_IndustryTemplatesTable<T>, 'templates'>(this, {
			templates: computed,
		});
	}

	public render() {
		const { className, styles, type, industry, templates, nameColumnLabel } = this.props;
		return (
			<div className={`${css(styleSheet.container, ...(styles || []))} industry-templates-table ${className || ''}`}>
				<div className={css(styleSheet.header)}>
					<div className={css(styleSheet.gripperCol)}>{}</div>
					<div className={css(styleSheet.nameCol)}>{nameColumnLabel || 'Name'}</div>
					<div className={css(styleSheet.industriesCol)}>Industries</div>
					<div className={css(styleSheet.creationDateCol)}>Created</div>
					<div className={css(styleSheet.actionsCol)}>Actions</div>
				</div>
				<DragDropContext onDragEnd={this.onDragEnd}>
					<Droppable direction='vertical' droppableId={`${industry}-${type}`} type='template'>
						{(droppableProvided: DroppableProvided) => {
							return (
								<Observer>
									{() => {
										return (
											<div ref={droppableProvided.innerRef} {...droppableProvided.droppableProps}>
												{this.templates?.map((template, index) => {
													return (
														<IndustryTemplatesTableRow
															index={index}
															key={template.id}
															onClick={this.onTemplateClicked(template as any)}
															onMenuItemClicked={this.onMenuItemClicked(template as any)}
															styles={[styleSheet.item]}
															template={template}
															type={type}
														/>
													);
												})}
											</div>
										);
									}}
								</Observer>
							);
						}}
					</Droppable>
				</DragDropContext>
				{!!templates.isLoading && <LoadingSpinner className={css(styleSheet.loadingSpinner)} type='large' />}
			</div>
		);
	}

	private get templates() {
		const { templates, type } = this.props;
		return templates[type];
	}

	private onDragEnd = (result: DropResult) => {
		const { templates, industry, logApiError, logEvent, errorMessages } = this.props;

		const toIndex = result.destination.index;
		const template = templates.published.getById(result.draggableId);
		const promise = templates.reorderPublishedTemplate(template, industry, toIndex);
		if (promise) {
			logEvent('Reorder', {
				from: templates.published.indexOf(template),
				id: template.id,
				industry,
				to: toIndex,
			});
			promise.catch((error: IOperationResultNoValue) => {
				logApiError('Reorder-Error', error);

				errorMessages.pushApiError(error);
			});
		}
	};

	private onTemplateClicked = (template: T) => (e: React.MouseEvent<HTMLElement>) => {
		this.props.onTemplateClicked?.(template, e);
	};

	private onMenuItemClicked = (template: T) => (menuItem: 'edit' | 'archive' | 'delete' | 'publish' | 'copy') => {
		this.props.onMenuItemClicked?.(template, menuItem);
	};
}

const IndustryTemplatesTableAsObserver = observer(_IndustryTemplatesTable);
const IndustryTemplatesTableWithContext = inject(ErrorMessagesViewModelKey)(IndustryTemplatesTableAsObserver);
const IndustryTemplatesTableWithNotificationService = withNotificationService(IndustryTemplatesTableWithContext);
export const IndustryTemplatesTable = withEventLogging(
	IndustryTemplatesTableWithNotificationService,
	'IndustryTemplatesTable'
);
